<script setup lang="ts">
import { computed } from "vue";
import DilemmaOptionInput from "./DilemmaOptionInput.vue";
import InformationText from "@/components/InformationText.vue";

type Props = {
  minOptions?: number;
  maxOptions?: number;
  label?: string;
};

const {
  minOptions = 2,
  maxOptions = 10,
  label = "Options",
} = defineProps<Props>();

const options = defineModel<string[]>({ required: true });

const hasDuplicateOptions = computed(() => {
  const filled = options.value
    .map((o) => o.trim().toLowerCase())
    .filter((o) => o !== "");
  return new Set(filled).size !== filled.length;
});

function addOption(): void {
  if (options.value.length < maxOptions) {
    options.value.push("");
  }
}

function removeOption(index: number): void {
  if (options.value.length > minOptions) {
    options.value.splice(index, 1);
  }
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <label class="font-medium text-kekkle-dark dark:text-slate-200">
      {{ label }}
    </label>

    <DilemmaOptionInput
      v-for="(_, index) in options"
      :key="index"
      v-model="options[index]"
      :placeholder="
        $t('pages.dilemmas.option_placeholder', 'Option ') + (index + 1)
      "
      :show-remove="options.length > minOptions"
      @remove="removeOption(index)"
    />

    <button
      v-if="options.length < maxOptions"
      type="button"
      class="mt-2 rounded-sm border-2 border-dashed border-kekkle-dark bg-kekkle-purple-light/30 p-2.5 text-xs font-bold text-kekkle-dark transition-all hover:bg-kekkle-purple-light/60 dark:border-slate-600 dark:bg-kekkle-dark-light dark:text-purple-300"
      @click.prevent="addOption"
    >
      + {{ $t("pages.dilemmas.add_option", "Add Option") }}
    </button>

    <InformationText
      v-if="hasDuplicateOptions"
      color="red"
      class="mt-1"
      :information="
        $t(
          'pages.dilemmas.duplicate_options_error',
          'Each option must be unique.',
        )
      "
    />
  </div>
</template>
